import React, { useEffect } from 'react';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { useTranslation } from 'react-i18next';
import mainScreen from 'assets/img/knowledge/0_main_screen.jpg';
import copyIntent from 'assets/img/knowledge/1_copy_intent.jpg';
import pdfImport from 'assets/img/knowledge/2_pdf import.jpg';
import cameraOcr from 'assets/img/knowledge/3_camera_ocr.mp4';
import fontSize from 'assets/img/knowledge/4_font_size.jpg';
import letterSpacing from 'assets/img/knowledge/5_letter_spacing.jpg';
import lineSpacing from 'assets/img/knowledge/6_line_spacing.jpg';
import mindMap from 'assets/img/knowledge/7_mind_map.jpg';
import ttsVideo from 'assets/img/knowledge/8_tts.mp4';

const Knowledge: React.FC = () => {
  const { t } = useTranslation();
  const ttsItems = t('knowledge.tts.tip_items', { returnObjects: true }) as string[];
  const mindMapSteps = t('knowledge.ai.steps', { returnObjects: true }) as string[];
  const perfectForItems = t('knowledge.ai.perfect_for_items', { returnObjects: true }) as string[];
  useEffect(() => {
    const handler = (e: Event) => {
      const target = e.target as HTMLAnchorElement;
      if (target && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const el = document.querySelector(target.getAttribute('href')!);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  return (
    <div className="knowledge-page">
      <style>{`
        .guide-hero { background: linear-gradient(135deg, var(--primary), #1B5694, #052B57); padding: 4rem 0; color: white; text-align: center; }
        .feature-card { background: white; border-radius: 15px; padding: 2rem; box-shadow: 0 8px 25px rgba(0,0,0,0.1); margin-bottom: 0.5rem; transition: transform .3s ease; }
        .feature-card:hover { transform: translateY(-5px); }
        .feature-icon { font-size: 3rem; color: var(--primary); margin-bottom: 1rem; }
        .demo-text { font-family: Arial, sans-serif; font-weight: bold; margin: 1rem 0; }
        .step-number { background: var(--primary); color: white; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; margin-right: 1rem; flex-shrink: 0; }
        .step-container { display: flex; align-items: flex-start; margin-bottom: 0.75rem; }
        .navigation-breadcrumb { background: #f8f9fa; padding: 1rem; border-radius: 10px; margin-bottom: 0.75rem; }
        .feature-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 0.5rem; margin-top: 0.5rem; }
        .quick-tip { background: #e8f4fd; border-left: 4px solid var(--primary); padding: 1rem; margin: 0.5rem 0; border-radius: 5px; }
        .ai-highlight { background: linear-gradient(135deg, var(--primary), #1B5694, #052B57); color: white; padding: 1rem; border-radius: 10px; margin: 0.75rem 0; }
        .screenshot-placeholder { background: #f8f9fa; border: 2px dashed #dee2e6; border-radius: 10px; padding: 2.1rem 2rem; text-align: center; margin: 0.5rem 0; color: #6c757d; }
        .screenshot-placeholder i { font-size: 3rem; margin-bottom: 1rem; color: #adb5bd; }
        .gif-placeholder { background: linear-gradient(135deg, #e3f2fd, #f3e5f5); border: 2px dashed #90a4ae; border-radius: 10px; padding: 1.35rem; text-align: center; margin: 0.5rem 0; color: #546e7a; }
        .gif-placeholder i { font-size: 2.5rem; margin-bottom: 1rem; color: #78909c; }
        .knowledge-page .section-shaped .shape-primary { background: linear-gradient(150deg, var(--primary) 15%, #1B5694 70%, #052B57 94%) !important; }
        .knowledge-page .section-shaped .shape-primary :nth-child(1) { background: #3f78b7 !important; }
        .knowledge-page .section-shaped .shape-primary :nth-child(2) { background: #2e6aa8 !important; }
        .knowledge-page .section-shaped .shape-primary :nth-child(3) { background: #2a5b8e !important; }
        .knowledge-page .section-shaped .shape-primary :nth-child(4) { background: #1f6fb0 !important; }
        .knowledge-page .section-shaped .shape-primary :nth-child(5) { background: #0f4a86 !important; }
        .knowledge-page .section-shaped .shape-primary :nth-child(6) { background: #3f78b7 !important; }
        .knowledge-page .section-shaped .shape-primary :nth-child(7) { background: #2e6aa8 !important; }
        .knowledge-page .section-shaped .shape-primary :nth-child(8) { background: #2a5b8e !important; }
        .knowledge-page .section-shaped .shape-primary :nth-child(9) { background: #1f6fb0 !important; }
        .knowledge-page .section-shaped .shape-primary :nth-child(10) { background: #0f4a86 !important; }
        /* Avoid setting background on the section to keep orbs visible (shape has z-index -1) */
        /* Make any purple bands use navy gradient */
        .knowledge-page .bg-gradient-primary { background: linear-gradient(150deg, var(--primary) 15%, #1B5694 70%, #052B57 94%) !important; }
        /* Override purple accents to navy */
        .knowledge-page .text-primary { color: var(--primary) !important; }
        .knowledge-page .bg-gradient-primary.text-white { background: linear-gradient(150deg, var(--primary) 15%, #1B5694 70%, #052B57 94%) !important; }
        /* Style AI highlight titles */
        .knowledge-page .ai-highlight h6 { color: white !important; font-size: 1.1rem !important; text-transform: uppercase !important; font-weight: 600 !important; }
        .card .card-body { font-size: 1.12rem; }
        .knowledge-page .row .card { margin-bottom: 0.5rem; }
        .knowledge-page .card.mb-4 { margin-bottom: 0.5rem !important; }
        /* Increase overall content size from "What is DysGo" downwards */
        #quickstart p, #quickstart li { font-size: 1.12rem; }
        #quickstart h4, #quickstart .h5 { font-size: 1.35rem; }
        #quickstart h2.h3 { font-size: 1.9rem !important; }
        /* Reduce large section margins */
                .knowledge-page .mb-5 { margin-bottom: 1rem !important; }
        /* Reduce gap between breadcrumb and content */
        .knowledge-page .navigation-breadcrumb { margin-bottom: 0.05rem; }
        .knowledge-page #quickstart { padding-top: 0.05rem; }
        /* Knowledge page media tweaks */
        .mindmap-image { margin-bottom: 0.75rem; }
        `}</style>
      <PublicNavbar />

      <section className="section section-hero section-shaped">
        <div className="shape shape-style-1 shape-primary">
          <span className="span-150"></span>
          <span className="span-50"></span>
          <span className="span-50"></span>
          <span className="span-75"></span>
          <span className="span-100"></span>
          <span className="span-75"></span>
          <span className="span-50"></span>
          <span className="span-100"></span>
          <span className="span-50"></span>
          <span className="span-100"></span>
        </div>
        <div className="page-header">
          <div className="container shape-container d-flex align-items-center py-lg">
            <div className="col px-0">
              <div className="row align-items-center justify-content-center">
                <div className="col-lg-8 text-center">
                  <h1 className="display-3 text-white mb-3">{t('knowledge.hero.title')}</h1>
                  <p className="lead text-white">{t('knowledge.hero.subtitle')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="separator separator-bottom separator-skew zindex-100">
          <svg x="0" y="0" viewBox="0 0 2560 100" preserveAspectRatio="none" version="1.1" xmlns="http://www.w3.org/2000/svg">
            <polygon className="fill-white" points="2560 0 2560 100 0 100"></polygon>
          </svg>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="navigation-breadcrumb">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><a href="/">{t('knowledge.breadcrumb.home')}</a></li>
                <li className="breadcrumb-item active" aria-current="page">{t('knowledge.breadcrumb.current')}</li>
              </ol>
            </nav>
          </div>
        </div>
      </section>

      {/* Text Input Methods */}
      <main className="section" id="quickstart">
        <div className="container">
          <section className="mb-5">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <h2 className="h3 text-success font-weight-bold mb-4">{t('knowledge.what.title')}</h2>
                <p className="lead text-muted mb-4">{t('knowledge.what.body')}</p>
                <img src={mainScreen} alt={t('knowledge.what.image_alt')} className="img-fluid rounded shadow-sm" style={{ maxWidth: '50%', display: 'block', margin: '0.5rem auto' }} />
              </div>
            </div>
          </section>

          <section className="mb-5">
            <h2 className="h3 text-success font-weight-bold mb-4">
              <i className="ni ni-cloud-upload-96 text-primary mr-2"></i>
              {t('knowledge.getting_text.title')}
            </h2>
            <div className="row">
              <div className="col-lg-4">
                <div className="card shadow border-0 mb-4"><div className="card-body">
                  <div className="step-container">
                    <div className="step-number">1</div>
                    <div>
                      <h4 className="h5 text-primary">{t('knowledge.getting_text.copy_intent.title')}</h4>
                      <p className="text-muted">{t('knowledge.getting_text.copy_intent.desc')}</p>
                      <img src={copyIntent} alt={t('knowledge.getting_text.copy_intent.image_alt')} className="img-fluid rounded shadow-sm" />
                      <div className="quick-tip"><strong>{t('knowledge.getting_text.copy_intent.tip_label')}</strong> {t('knowledge.getting_text.copy_intent.tip_text')}</div>
                    </div>
                  </div>
                </div></div>
              </div>
              <div className="col-lg-4">
                <div className="card shadow border-0 mb-4"><div className="card-body">
                  <div className="step-container">
                    <div className="step-number">2</div>
                    <div>
                      <h4 className="h5 text-primary">{t('knowledge.getting_text.pdf_import.title')}</h4>
                      <p className="text-muted">{t('knowledge.getting_text.pdf_import.desc')}</p>
                      <img src={pdfImport} alt={t('knowledge.getting_text.pdf_import.image_alt')} className="img-fluid rounded shadow-sm" />
                      <div className="quick-tip"><strong>{t('knowledge.getting_text.pdf_import.tip_label')}</strong> {t('knowledge.getting_text.pdf_import.tip_text')}</div>
                    </div>
                  </div>
                </div></div>
              </div>
              <div className="col-lg-4">
                <div className="card shadow border-0 mb-4"><div className="card-body">
                  <div className="step-container">
                    <div className="step-number">3</div>
                    <div>
                      <h4 className="h5 text-primary">{t('knowledge.getting_text.camera_ocr.title')}</h4>
                      <p className="text-muted">{t('knowledge.getting_text.camera_ocr.desc')}</p>
                      <video className="w-100 rounded shadow-sm" src={cameraOcr} autoPlay playsInline muted loop />
                      <div className="quick-tip"><strong>{t('knowledge.getting_text.camera_ocr.tip_label')}</strong> {t('knowledge.getting_text.camera_ocr.tip_text')}</div>
                    </div>
                  </div>
                </div></div>
              </div>
            </div>
          </section>

          {/* Customization Options */}
          <section className="mb-5">
            <h2 className="h3 text-success font-weight-bold mb-4">
              <i className="ni ni-settings-gear-65 text-primary mr-2"></i>
              {t('knowledge.customization.title')}
            </h2>
            <div className="row">
              <div className="col-lg-4"><div className="card shadow border-0 mb-4"><div className="card-body text-center">
                <div className="icon icon-shape bg-gradient-primary text-white rounded-circle mb-3"><i className="ni ni-bold"></i></div>
                <h4 className="h5 text-primary">{t('knowledge.customization.font_size.title')}</h4>
                <p className="text-muted">{t('knowledge.customization.font_size.desc')}</p>
                <img src={fontSize} alt={t('knowledge.customization.font_size.image_alt')} className="img-fluid rounded shadow-sm" />
                <div className="quick-tip"><strong>{t('knowledge.customization.font_size.tip_label')}</strong> {t('knowledge.customization.font_size.tip_text')}</div>
              </div></div></div>
              <div className="col-lg-4"><div className="card shadow border-0 mb-4"><div className="card-body text-center">
                <div className="icon icon-shape bg-gradient-success text-white rounded-circle mb-3"><i className="ni ni-chart-bar-32"></i></div>
                <h4 className="h5 text-primary">{t('knowledge.customization.line_spacing.title')}</h4>
                <p className="text-muted">{t('knowledge.customization.line_spacing.desc')}</p>
                <img src={lineSpacing} alt={t('knowledge.customization.line_spacing.image_alt')} className="img-fluid rounded shadow-sm" />
                <div className="quick-tip"><strong>{t('knowledge.customization.line_spacing.tip_label')}</strong> {t('knowledge.customization.line_spacing.tip_text')}</div>
              </div></div></div>
              <div className="col-lg-4"><div className="card shadow border-0 mb-4"><div className="card-body text-center">
                <div className="icon icon-shape bg-gradient-warning text-white rounded-circle mb-3"><i className="ni ni-chart-pie-35"></i></div>
                <h4 className="h5 text-primary">{t('knowledge.customization.letter_spacing.title')}</h4>
                <p className="text-muted">{t('knowledge.customization.letter_spacing.desc')}</p>
                <img src={letterSpacing} alt={t('knowledge.customization.letter_spacing.image_alt')} className="img-fluid rounded shadow-sm" />
                <div className="quick-tip"><strong>{t('knowledge.customization.letter_spacing.tip_label')}</strong> {t('knowledge.customization.letter_spacing.tip_text')}</div>
              </div></div></div>
            </div>
          </section>

          {/* Text To Speech */}
          <section className="mb-5">
            <h2 className="h3 text-success font-weight-bold mb-4">
              <i className="bi bi-volume-up text-primary mr-2"></i>
              {t('knowledge.tts.title')}
            </h2>
            <div className="card shadow border-0"><div className="card-body">
              <div className="row align-items-center">
                <div className="col-lg-6">
                  <h4 className="h5 text-primary">{t('knowledge.tts.listen_title')}</h4>
                  <p className="text-muted">{t('knowledge.tts.listen_desc')}</p>
                  <div className="step-container"><div className="step-number">1</div><div>{t('knowledge.tts.step1')}</div></div>
                  <div className="step-container"><div className="step-number">2</div><div>{t('knowledge.tts.step2')}</div></div>
                  <div className="step-container"><div className="step-number">3</div><div>{t('knowledge.tts.step3')}</div></div>
                  <div className="quick-tip"><strong>{t('knowledge.tts.tip_label')}</strong><ul>
                    {Array.isArray(ttsItems) && ttsItems.map((item, idx) => (<li key={idx}>{item}</li>))}
                  </ul></div>
                </div>
                <div className="col-lg-6">
                  <video className="rounded shadow-sm" src={ttsVideo} autoPlay playsInline muted loop style={{ maxWidth: '50%', width: '50%', display: 'block', margin: '0.5rem auto' }} />
                </div>
              </div>
            </div></div>
          </section>

          {/* AI Features */}
          <section className="mb-5">
            <h2 className="h3 text-success font-weight-bold mb-4">
              <i className="ni ni-single-02 text-primary mr-2"></i>
              {t('knowledge.ai.title')}
            </h2>
            <div className="card shadow border-0"><div className="card-body">
              <div className="row align-items-center">
                <div className="col-lg-6">
                  <h4 className="h5 text-primary">{t('knowledge.ai.mindmap_title')}</h4>
                  <p className="text-muted">{t('knowledge.ai.mindmap_desc')}</p>
                  <img src={mindMap} alt={t('knowledge.ai.mindmap_image_alt')} className="img-fluid rounded shadow-sm mindmap-image" />
                  <div className="step-container"><div className="step-number">1</div><div>{mindMapSteps?.[0]}</div></div>
                  <div className="step-container"><div className="step-number">2</div><div>{mindMapSteps?.[1]}</div></div>
                  <div className="step-container"><div className="step-number">3</div><div>{mindMapSteps?.[2]}</div></div>
                  <div className="ai-highlight"><h6><i className="ni ni-lightbulb mr-2"></i>{t('knowledge.ai.teacher_highlight_title')}</h6><p className="mb-0">{t('knowledge.ai.teacher_highlight_text')}</p></div>
                </div>
                <div className="col-lg-6">
                  <div className="quick-tip"><strong>{t('knowledge.ai.perfect_for_label')}</strong><ul>
                    {Array.isArray(perfectForItems) && perfectForItems.map((item, idx) => (<li key={idx}>{item}</li>))}
                  </ul></div>
                  <div className="ai-highlight"><h6><i className="ni ni-refresh mr-2"></i>{t('knowledge.ai.export_highlight_title')}</h6><p className="mb-0">{t('knowledge.ai.export_highlight_text')}</p></div>
                </div>
              </div>
            </div></div>
          </section>

          {/* Support & Feedback */}
          <section className="mb-5">
            <div className="card bg-gradient-primary shadow border-0"><div className="card-body text-center">
              <h3 className="text-white">{t('knowledge.support.cta_title')}</h3>
              <p className="lead text-white-50">{t('knowledge.support.cta_subtitle')}</p>
              <div className="row justify-content-center"><div className="col-lg-8">
                <p className="text-white-50">{t('knowledge.support.body')}</p>
                <a href="/" className="btn btn-white btn-lg mr-3"><i className="ni ni-shop mr-2"></i>{t('knowledge.support.btn_back_home')}</a>
                <a href="/#download" className="btn btn-outline-white btn-lg"><i className="ni ni-cloud-download-95 mr-2"></i>{t('knowledge.support.btn_download')}</a>
              </div></div>
            </div></div>
          </section>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default Knowledge;


